import { FC, useState, useMemo, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';
import { useTherapistAppointments } from '../../../../hooks/useTherapistDashboard';
import type { ScheduleFilters } from '../../../../types/therapist-dashboard.types';
import type { ViewMode } from './types';
import { formatDate, getStartOfWeek, getEndOfWeek, getDaysInMonth } from './dateUtils';
import { ViewToggle } from './ViewToggle';
import { DateNavigation } from './DateNavigation';
import { DayView } from './DayView';
import { WeekView } from './WeekView';
import { MonthView } from './MonthView';
import { StatusLegend } from './StatusLegend';
import { ScheduleSummary } from './ScheduleSummary';

export const ScheduleSection: FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [currentDate, setCurrentDate] = useState(new Date());

  // Calculate date range based on view mode
  const dateRange = useMemo((): { startDate: string; endDate: string } => {
    switch (viewMode) {
      case 'day':
        return {
          startDate: formatDate(currentDate),
          endDate: formatDate(currentDate),
        };
      case 'week':
        return {
          startDate: formatDate(getStartOfWeek(currentDate)),
          endDate: formatDate(getEndOfWeek(currentDate)),
        };
      case 'month': {
        // Extend range to include padding days for full calendar display
        const monthDays = getDaysInMonth(currentDate);
        const firstDay = monthDays[0] ?? currentDate;
        const lastDay = monthDays[monthDays.length - 1] ?? currentDate;
        return {
          startDate: formatDate(firstDay),
          endDate: formatDate(lastDay),
        };
      }
    }
  }, [viewMode, currentDate]);

  const filters: ScheduleFilters = useMemo(
    () => ({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      view: viewMode,
    }),
    [dateRange, viewMode]
  );

  const { data: appointments, isLoading, error, refetch } = useTherapistAppointments(filters);

  // Navigation handlers
  const handlePrevious = useCallback(() => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      switch (viewMode) {
        case 'day':
          d.setDate(d.getDate() - 1);
          break;
        case 'week':
          d.setDate(d.getDate() - 7);
          break;
        case 'month':
          d.setMonth(d.getMonth() - 1);
          break;
      }
      return d;
    });
  }, [viewMode]);

  const handleNext = useCallback(() => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      switch (viewMode) {
        case 'day':
          d.setDate(d.getDate() + 1);
          break;
        case 'week':
          d.setDate(d.getDate() + 7);
          break;
        case 'month':
          d.setMonth(d.getMonth() + 1);
          break;
      }
      return d;
    });
  }, [viewMode]);

  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Schedule</h2>
            <p className="text-gray-600 dark:text-slate-400 mt-1">
              View and manage your appointments
            </p>
          </div>
        </div>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Schedule</h2>
            <p className="text-gray-600 dark:text-slate-400 mt-1">
              View and manage your appointments
            </p>
          </div>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-8 text-center border border-red-200 dark:border-red-800">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
            Failed to Load Schedule
          </h3>
          <p className="text-red-600 dark:text-red-300 mb-4">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const appointmentList = appointments || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Schedule</h2>
            <p className="text-gray-600 dark:text-slate-400 mt-1">
              View and manage your appointments
            </p>
          </div>
          <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
        </div>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <DateNavigation
            currentDate={currentDate}
            viewMode={viewMode}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onToday={handleToday}
          />
          <StatusLegend />
        </div>
      </div>

      {/* Calendar View */}
      <div className="overflow-x-auto">
        {viewMode === 'day' && <DayView date={currentDate} appointments={appointmentList} />}
        {viewMode === 'week' && <WeekView date={currentDate} appointments={appointmentList} />}
        {viewMode === 'month' && <MonthView date={currentDate} appointments={appointmentList} />}
      </div>

      {/* Summary */}
      <ScheduleSummary viewMode={viewMode} appointments={appointmentList} />
    </div>
  );
};
